import React, { useContext, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import Header from "./Header";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const setImage = (idx) => {
    setSelectedImage(idx);
  };

  const thumbs = Array.isArray(product.images) && product.images.length
    ? product.images
    : [product.image];

  const rawPrice = Number(product.price);
  const originalPrice = Number.isFinite(rawPrice) ? rawPrice : 0;
  const salePrice = Number.isFinite(rawPrice) ? rawPrice * 0.75 : 0;
  const colorsCount = Array.isArray(product.colors) ? product.colors.length : 0;
  const isNew = product.id % 3 === 0;

  const ratingValue = Number(product.rating) || 0;
  const fullStars = Math.max(0, Math.min(5, Math.floor(ratingValue)));
  const stars = Array.from({ length: 5 }, (_, i) => i < fullStars);

  return (
    <div className="product-detail">
      <Header />

      <div className="product-detail__top">
        <Link to="/shop" onClick={toTop} className="product-detail__back">
          ← Back to Results
        </Link>
        <div className="product-detail__crumb">New Arrivals</div>
      </div>

      <div className="product-detail__container">
        <div className="gallery-column">
          <div className="thumbs">
            {thumbs.map((img, idx) => (
              <button
                key={`${img}-${idx}`}
                className={`thumb ${idx === selectedImage ? "active" : ""}`}
                onClick={() => setImage(idx)}
                aria-label={`Show image ${idx + 1}`}
                type="button"
              >
                <img
                  src={img}
                  alt={`${product.name} thumbnail ${idx + 1}`}
                  loading="lazy"
                  decoding="async"
                  width="88"
                  height="88"
                />
              </button>
            ))}
          </div>

          <div className="main-image">
            <img
              src={thumbs[selectedImage]}
              alt={product.name}
              decoding="async"
              width="920"
              height="780"
            />
          </div>
        </div>

        <aside className="product-info">
          {isNew && <div className="product-badge">NEW ARRIVAL</div>}
          <div className="product-header">
            <div className="product-brand">{product.brand || "Brand"}</div>
            <div className="product-titleRow">
              <h1 className="product-name">{product.name}</h1>
              <button
                type="button"
                className="wish"
                aria-label="Add to wishlist"
              >
                <i className="far fa-heart" />
              </button>
            </div>
          </div>

          <div className="product-pricing">
            <div className="product-prices">
              <span className="price-original">
                ${originalPrice.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="price-sale">
                ${salePrice.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="price-discount">25% off</span>
            </div>
          </div>

          <div className="product-ratingRow">
            <div className="stars" aria-label={`Rating ${ratingValue} out of 5`}>
              {stars.map((filled, idx) => (
                <span key={idx} className={filled ? "star filled" : "star"}>
                  ★
                </span>
              ))}
            </div>
            <div className="reviews">
              {product.reviews ? `${product.reviews} Reviews` : "No Reviews"}
            </div>
            <div className="sku">Item No. {String(product.id).padStart(6, "0")}</div>
          </div>

          <div className="product-options">
            <div className="option-row">
              <div className="option-left">{colorsCount} Color</div>
              <select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                aria-label="Choose color"
              >
                {(product.colors || []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="option-row">
              <div className="option-left">Size</div>
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                aria-label="Choose size"
              >
                {(product.size || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="shipping">
            <div className="shipping-title">SHIPPING + DELIVERY</div>
            <div className="shipping-text">
              Ready to ship via Standard Delivery
            </div>
            <Link to="/shipping" onClick={toTop} className="shipping-link">
              Shipping Details
            </Link>
          </div>

          <div className="qtyRow">
            <div className="qtyLabel">Quantity</div>
            <div className="qtyControls">
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                −
              </button>
              <div className="qtyValue">{quantity}</div>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => setQuantity((q) => q + 1)}
              >
                +
              </button>
            </div>
          </div>

          <button className="add-to-cart" onClick={handleAddToCart}>
            Add to Cart
          </button>

          <div className="product-description">
            {(product.description || []).map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
          </div>

          <div className="details-care">
            <div className="details">
              <h4>DETAILS AND CARE</h4>
              <ul>
                {(product.Features || []).map((f, idx) => (
                  <li key={idx}>{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
